import React, { useRef, useMemo } from 'react';
import LazyLoad from 'react-lazyload';
import {
  always,
  cond,
  equals,
  isEmpty,
  isNil,
  filter,
  flatten,
  map,
  path,
  pathEq,
  pathOr,
  pipe,
  prop,
  values,
  includes,
  reject,
} from 'ramda';
import { useIntl } from 'gatsby-plugin-intl';
import { findByName, findByType } from '../../utils';
import { BLOCK_ELEMENTS, CHART_TYPES, ELEMENTS_TYPES, REPORT_CONTENT_ID } from '../../utils/constants';
import { renderWhenTrue, renderWhenTrueOtherwise } from '../../utils/rendering';
import { CardLeft } from '../cardLeft';
import { getRadialConfig } from '../charts/configs/radial.config';
import {
  getHorizontalBarConfig,
  getVerticalBarConfig,
  getHorizontalStackConfig,
  getVerticalStackConfig,
} from '../charts/configs/bar.config';
import { getLineChartConfig } from '../charts/configs/line.config';
import { getPieConfig } from '../charts/configs/pie.config';
import { getMapConfig } from '../charts/configs/map.config';
import { getSlopeConfig } from '../charts/configs/slope.config';
import { VerticalBarChart, HorizontalBarChart, schemaBarParser } from '../charts/barChart';
import { VerticalStackChart, HorizontalStackChart } from '../charts/stackChart';
import { SlopeChart } from '../charts/slopeChart';
import { SpiderChart, basicSpiderParser } from '../charts/spiderChart';
import { BoxPlotChart, basicBoxPlotParser } from '../charts/boxPlotChart';
import { RadialChart, schemaRadialParser } from '../charts/radialChart';
import { MapChart, basicMapParser } from '../charts/mapChart';
import { BubbleChart, basicBubbleParser } from '../charts/bubbleChart';
import { PieChart, basicPieParser } from '../charts/pieChart';
import { LineChart, basicLineParser } from '../charts/lineChart'
import {
  Container,
  Side,
  Content,
  Note,
  MarkdownContent,
  ChartWrapper,
  CardToggleButton,
  CardContainer,
  ChartLoader,
  ChartNotes,
} from './chart.styles';
import { ChartTooltipContextProvider } from '../../store/ChartTooltipContext';
import { useCardLeft } from '../../hooks/useCardLeft';
import { useBreakpointsContext } from '../../store/BreakpointsContext';
import { useScrollIntoView } from '../../hooks/useScrollIntoView';
import { ChartContextProvider } from '../../store/ChartContext';
import { useWindowLocation } from '../../hooks/useWindowLocation';
import { CardToggleCloseIcon } from '../cardLeft/cardLeft.styles';
import { AXIS_LONG_NAME, MAX_X_AXIS_SCORE, NUMBER_OF_OBSERVATIONS } from './chart.constant';
import { isPuppeteer } from '../../utils/media';

const {
  BAR,
  BAR_GROUPED,
  BAR_STACKED,
  BAR_VERTICAL,
  BAR_VERTICAL_GROUPED,
  BAR_VERTICAL_STACKED,
  BUBBLE,
  DONUT,
  MAP,
  RADIAL,
  SLOPE,
  SPIDER,
  BOX,
  LINE
} = CHART_TYPES;

const renderNote = (note, type) => {
  const { formatMessage } = useIntl();
  return renderWhenTrue(() => <Note type={type}><MarkdownContent>{formatMessage({ id: note })}</MarkdownContent></Note>)(!!note);
};

const renderChart = ({ chartData, value } = {}, { type, tabs, height, isMobile, isSet }) => {
  if (!isSet) return;
  const chartType = path(['tags', 'chartType', 0], chartData);
  const records = pathOr({}, ['records'], chartData);
  const chartNotes = pathOr(null, ['meta', 'customData', 'notes'], chartData);
  const maxXAxisScore = pathOr(null, ['meta', 'customData', MAX_X_AXIS_SCORE], chartData);

  const fieldsName = pipe(pathOr({}, ['fields']), values, map(prop('name')))(chartData);

  const fieldsIds = Object.keys(records[0] || {});
  const hasLongName = includes(AXIS_LONG_NAME, fieldsIds);
  const hasObservations = includes(NUMBER_OF_OBSERVATIONS, fieldsIds);
  const observations = hasObservations ? { observations: NUMBER_OF_OBSERVATIONS } : {};

  const [axis, ...series] = hasLongName ? reject(includes(AXIS_LONG_NAME), fieldsIds) : fieldsIds;
  const intl = useIntl();

  const data = pipe(
    values,
    filter((item) => !isNil(item[axis]))
  )(records);

  const labels = {};

  fieldsIds.map((id, index) => (labels[id] = fieldsName[index]));

  const title = path(['meta', 'customTitle'], value) || path(['meta', 'description'], value);
  const componentData = {
    axis,
    longName: hasLongName ? AXIS_LONG_NAME : axis,
    series,
    labels,
    data,
    title,
    fields: fieldsName,
    maxXAxisScore,
    ...observations,
  };

  const [Component, config = {}, parser] = cond([
    [equals(BAR), always([HorizontalBarChart, getHorizontalBarConfig(type, componentData), schemaBarParser])],
    [equals(BAR_GROUPED), always([HorizontalBarChart, getHorizontalBarConfig(type, componentData), schemaBarParser])],
    [
      equals(BAR_STACKED),
      always([HorizontalStackChart, getHorizontalStackConfig(type, componentData), schemaBarParser]),
    ],
    [equals(BAR_VERTICAL), always([VerticalBarChart, getVerticalBarConfig(type, componentData), schemaBarParser])],
    [
      equals(BAR_VERTICAL_GROUPED),
      always([VerticalBarChart, getVerticalBarConfig(type, componentData), schemaBarParser]),
    ],
    [
      equals(BAR_VERTICAL_STACKED),
      always([VerticalStackChart, getVerticalStackConfig(type, componentData), schemaBarParser]),
    ],
    [equals(BUBBLE), always([BubbleChart, {}, basicBubbleParser])],
    [equals(DONUT), always([PieChart, getPieConfig(type), basicPieParser])],
    [equals(MAP), always([MapChart, getMapConfig(type, isMobile), basicMapParser])],
    [equals(RADIAL), always([RadialChart, getRadialConfig(type), schemaRadialParser])],
    [equals(SLOPE), always([SlopeChart, getSlopeConfig(type, componentData)])],
    [equals(SPIDER), always([SpiderChart, {}, basicSpiderParser])],
    [equals(BOX), always([BoxPlotChart, {}, basicBoxPlotParser])],
    [equals(LINE), always([LineChart, getLineChartConfig(type, componentData), basicLineParser])],
  ])(chartType);

  if (!Component) return null;

  return (
    <>
      <Component
        data={componentData}
        config={config}
        parser={parser}
        width={'100%'}
        height={config.chartHeight || height}
        tabs={tabs}
      />
      {renderWhenTrue(always(<ChartNotes>{chartNotes && intl.formatMessage({ id: chartNotes })}</ChartNotes>))(!!chartNotes)}
    </>
  );
};

export const Chart = ({ elements, tabs, className, height = 400, canShare = true }) => {
  const chart = findByName(BLOCK_ELEMENTS.CHART, elements);
  const card = findByName(BLOCK_ELEMENTS.CARD, elements);
  const { value: type = 'short' } = findByName(BLOCK_ELEMENTS.WIDTH, elements);
  const { value: note } = findByName(BLOCK_ELEMENTS.NOTE, elements);
  const chartType = path(['chartData', 'tags', 'chartType', 0], chart);
  const chartRef = useRef(null);
  const getWindowLocation = useWindowLocation();
  const windowLocation = getWindowLocation() || {};
  const chartId = useMemo(() => decodeURIComponent(windowLocation.search).split('=')[1], [windowLocation.search]);
  const shouldScroll = !isNil(chartId) && pathEq(['current', 'id'], chartId)(chartRef);

  const { isMobile, isSet } = useBreakpointsContext();
  const cardLeft = useCardLeft();

  useScrollIntoView(chartRef, shouldScroll, true);

  const renderSide = ({ items, value }) =>
    renderWhenTrue(() => {
      const elements = items || pipe(map(prop('elements')), flatten)(value);
      const { value: markdown } = findByType(ELEMENTS_TYPES.MARKDOWN, elements);
      const { value: connection } = findByType(ELEMENTS_TYPES.CONNECTION, elements);

      if (isEmpty(markdown) && isEmpty(connection)) return null;

      return <CardLeft elements={elements} />;
    })(!isEmpty(value || items));

  const placeholder = (height) => <ChartLoader height={height}>Loading...</ChartLoader>;
  const renderLazyLoad = renderWhenTrueOtherwise(
    () => (
      <LazyLoad
        height={height}
        scrollContainer={`#${REPORT_CONTENT_ID}`}
        offset={500}
        placeholder={placeholder(height)}
        once
      >
        {renderChart(chart, { type, tabs, height, isMobile, isSet })}
      </LazyLoad>
    ),
    () => renderChart(chart, { type, tabs, height, isMobile, isSet })
  );

  return (
    <ChartTooltipContextProvider>
      <ChartContextProvider values={{ chart, canShare: canShare && !isPuppeteer }}>
        <Container chartWidth={type} chartType={chartType} className={className} id={chart.id} ref={chartRef}>
          <Content>
            <Side>
              {renderWhenTrue(
                always(
                  <CardToggleButton isVisible={cardLeft.isVisible} onClick={cardLeft.toggleVisibility}>
                    <CardToggleCloseIcon />
                  </CardToggleButton>
                )
              )(!isEmpty(card.value))}
              <CardContainer isVisible={cardLeft.isVisible} ref={cardLeft.containerRef}>
                {renderSide(card)}
              </CardContainer>
            </Side>
            <ChartWrapper>{renderLazyLoad(!isPuppeteer)}</ChartWrapper>
          </Content>
          {renderNote(note, type)}
        </Container>
      </ChartContextProvider>
    </ChartTooltipContextProvider>
  );
};
